/**
 * What a kitchen display says about ITSELF, above the board (ORDR-42).
 *
 * Every state that is not a live board is said in words. A revoked display, an
 * unenrolled one and a feed that is not answering must never look like a quiet
 * kitchen with no tickets.
 */

import { useState, type FormEvent, type ReactElement } from "react";
import type { KitchenStationStatus } from "./useKitchenStation.js";

export interface KitchenStatusProps {
  readonly status: KitchenStationStatus;
  readonly onEnrol: (deviceId: string) => boolean;
}

const NOTICE = "m-3 rounded-[var(--border-radius-md)] bg-[var(--color-neutral-100)] p-3 text-sm";

export function KitchenStatus({ status, onEnrol }: KitchenStatusProps): ReactElement | null {
  switch (status.kind) {
    case "live":
      return null;
    case "loading":
      return (
        <p role="status" data-testid="kitchen-loading" className={NOTICE}>
          Connecting to the kitchen's orders
        </p>
      );
    case "no-site":
      return (
        <p role="alert" data-testid="kitchen-no-site" className={NOTICE}>
          This venue has no kitchen site set up, so there is no board to show.
        </p>
      );
    case "needs-device":
      return <EnrolDevice onEnrol={onEnrol} />;
    case "reconnecting":
      return (
        <p role="alert" data-testid="kitchen-reconnecting" className={NOTICE}>
          The kitchen's orders are not answering. Retrying; what is on screen may be out of date.
          {status.correlationId !== null && ` Reference: ${status.correlationId}`}
        </p>
      );
    case "not-enrolled":
      return (
        <div role="alert" data-testid="kitchen-not-enrolled" className={NOTICE}>
          <p>This display is not enrolled at this site, so it is not showing any orders.</p>
          <EnrolDevice onEnrol={onEnrol} />
        </div>
      );
    case "revoked":
      return (
        <p role="alert" data-testid="kitchen-revoked" className={NOTICE}>
          This display was revoked. It has stopped showing orders. Ask a manager to enrol a display.
        </p>
      );
  }
}

function EnrolDevice({ onEnrol }: { onEnrol: (deviceId: string) => boolean }): ReactElement {
  const [value, setValue] = useState("");
  const [rejected, setRejected] = useState(false);

  function submit(event: FormEvent): void {
    event.preventDefault();
    setRejected(!onEnrol(value));
  }

  return (
    <form data-testid="kitchen-enrol" onSubmit={submit} className={`${NOTICE} flex flex-col gap-2`}>
      <label htmlFor="kds-device-id">Display device id (from the venue set-up output)</label>
      <input
        id="kds-device-id"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        className="rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-2 py-1"
      />
      {rejected && <p role="alert">That is not a device id. It looks like 8-4-4-4-12 hexadecimal characters.</p>}
      <button type="submit" className="self-start rounded-[var(--border-radius-md)] border px-3 py-1">
        Save this display
      </button>
    </form>
  );
}
